<template>
  <div class="w-full shrink-0 px-2 pb-4 lg:pb-10">
    <div
      class="relative mx-auto flex h-14 w-full max-w-md flex-row items-center justify-center gap-2 lg:max-w-3xl"
    >
      <BaseIconButton
        icon="keyboard_arrow_left"
        width="wide"
        shape="round"
        size="M"
        color="tonal"
        class="lg:hidden"
        v-tooltip="backLabel ?? $t('common.back')"
        :disabled="backDisabled"
        @click="emit('back')"
      />
      <BaseTextButton
        :disabled="backDisabled"
        size="M"
        class="hidden lg:inline-flex"
        @click="emit('back')"
        icon="keyboard_arrow_left"
        width="wide"
        color="tonal"
      >
        {{ backLabel ?? $t('common.back') }}
      </BaseTextButton>
      <div
        class="bg-surface-container-lowest shadow-1 flex h-full grow flex-row justify-center rounded-full px-2 sm:min-w-sm sm:grow-0"
      >
        <div
          class="relative hidden flex-row items-center justify-center overflow-hidden lg:flex"
          :style="{ width: wrapperWidth }"
        >
          <div
            ref="container"
            class="no-scrollbar flex w-fit max-w-full flex-row items-center justify-start overflow-x-auto px-2 py-2"
          >
            <button
              v-for="(item, index) in items"
              :key="item.id"
              type="button"
              class="group h-7 cursor-pointer px-[1px]"
              :class="[currentIndex == index && 'px-[3px]!']"
              @click="emit('select', item.id)"
              @mouseenter="hoveredIndex = index"
              @mouseleave="hoveredIndex = null"
            >
              <div
                class="bg-surface-container-highest ease-fast-effect relative flex h-3 w-2 items-center justify-center overflow-hidden rounded-full transition-all duration-100"
                :class="[
                  currentIndex == index && 'outline-tertiary-container outline-3 outline-offset-1',
                  item.finished && 'bg-secondary-container!',
                  hoveredIndex == index && 'h-7 w-6',
                  sizedIndexes[0].has(index) && 'h-6 w-5',
                  sizedIndexes[1].has(index) && 'h-5 w-4',
                  sizedIndexes[2].has(index) && 'h-4 w-3',
                ]"
              >
                <BaseStateLayer :type="item.finished ? 'secondary-container' : 'surface'" />
                <BaseIcon
                  v-if="item.finished"
                  size="14"
                  class="text-on-secondary-container ease-fast-spatial opacity-0 transition-opacity duration-200"
                  :class="[
                    hoveredIndex != null &&
                      index - 3 <= hoveredIndex &&
                      hoveredIndex <= index + 3 &&
                      'opacity-100!',
                    currentIndex == index && 'text-on-primary-container!',
                  ]"
                  >check</BaseIcon
                >
              </div>
            </button>
          </div>
          <div
            class="from-surface-container-lowest absolute left-0 h-7 w-2 bg-gradient-to-r to-transparent"
          ></div>
          <div
            class="to-surface-container-lowest absolute right-0 h-7 w-2 bg-gradient-to-r from-transparent"
          ></div>
        </div>
        <div class="flex flex-row items-center lg:pl-2">
          <span class="text-label-small text-on-surface-variant mr-2"
            >{{ currentIndex + 1 }}/{{ items.length }}
            {{ $t('common.question', items.length) }}</span
          >
          <BaseCircularProgressIndictator
            class="lg:hidden"
            :percentage="percentage"
            :thickness="5"
            :radius="14"
          />
        </div>
      </div>
      <BaseIconButton
        icon="keyboard_arrow_right"
        width="wide"
        color="tonal"
        size="M"
        class="lg:hidden"
        v-tooltip="nextLabel ?? $t('common.next')"
        :disabled="nextDisabled"
        @click="emit('next')"
      />
      <BaseTextButton
        :disabled="nextDisabled"
        size="M"
        class="hidden lg:inline-flex"
        @click="emit('next')"
        trailing-icon="keyboard_arrow_right"
        width="wide"
        color="tonal"
      >
        {{ nextLabel ?? $t('common.next') }}
      </BaseTextButton>
    </div>
  </div>
</template>
<script lang="ts" setup>
  import { computed, onMounted, ref, useTemplateRef } from 'vue'
  import { useElementBounding } from '@vueuse/core'
  import BaseIcon from '@/core/components/base/icon/BaseIcon.vue'
  import BaseIconButton from '@/core/components/base/button/icon-button/BaseIconButton.vue'
  import BaseStateLayer from '@/core/components/base/state-layer/BaseStateLayer.vue'
  import BaseCircularProgressIndictator from '@/core/components/base/circlular-progress-indicator/BaseCircularProgressIndictator.vue'
  import { useHoverScroll } from '@/core/composables/useHoverScroll'
  import BaseTextButton from '../base/button/text-button/BaseTextButton.vue'

  export type StepNavigationItem = {
    id: string
    finished: boolean
  }

  interface Props {
    items: ReadonlyArray<StepNavigationItem>
    currentId: string
    backDisabled?: boolean
    nextDisabled?: boolean
    backLabel?: string
    nextLabel?: string
  }

  const props = defineProps<Props>()

  const emit = defineEmits<{
    back: []
    next: []
    select: [id: string]
  }>()

  const currentIndex = computed(() => props.items.findIndex((item) => item.id === props.currentId))

  const hoveredIndex = ref<number | null>(null)

  const sizedIndexes = computed(() => {
    const result: Array<Set<number>> = []
    for (let index = 1; index <= 3; index++) {
      const indexes = new Set<number>()
      if (hoveredIndex.value == null) {
        result.push(indexes)
        continue
      }

      if (hoveredIndex.value - index >= 0) {
        indexes.add(hoveredIndex.value - index)
      }

      if (hoveredIndex.value + index < props.items.length) {
        indexes.add(hoveredIndex.value + index)
      }
      result.push(indexes)
    }

    return result
  })

  const percentage = computed(() => {
    if (props.items.length === 0 || currentIndex.value < 0) {
      return 0
    }
    return ((currentIndex.value + 1) / props.items.length) * 100
  })

  const wrapperWidth = ref('auto')
  const container = useTemplateRef('container')
  const { width } = useElementBounding(container)
  onMounted(() => {
    wrapperWidth.value = width.value + 64 + 16 + 'px'
  })

  useHoverScroll(container)
</script>
